import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const navigation = [
    {
      name: 'Solutions',
      href: '/solutions',
      dropdown: [
        { name: 'OEM Dropship Enablement', href: '/solutions#oem' },
        { name: 'SMB-to-Enterprise', href: '/solutions#smb' },
        { name: 'Financial Integration', href: '/solutions#financial' },
        { name: 'Insurance Integration', href: '/solutions#insurance' }
      ]
    },
    {
      name: 'Products',
      href: '/products',
      dropdown: [
        { name: 'Agent-Driven Platform', href: '/products#platform' },
        { name: 'Marketplace Creation', href: '/products#marketplace' },
        { name: 'Financial Orchestration', href: '/products#financial' },
        { name: 'Analytics & Intelligence', href: '/products#analytics' }
      ]
    },
    {
      name: 'Industries',
      href: '/industries',
      dropdown: [
        { name: 'Manufacturing', href: '/industries#manufacturing' },
        { name: 'Construction', href: '/industries#construction' },
        { name: 'Healthcare', href: '/industries#healthcare' },
        { name: 'Financial Services', href: '/industries#financial' }
      ]
    },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Resources', href: '/resources' }
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200">
              <img 
                src="https://customer-assets.emergentagent.com/job_flexicred-platform/artifacts/kngvzlpr_Transparent%20Logo.png" 
                alt="CredFlexi Logo" 
                className="h-20 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              <div key={item.name} className="relative group">
                {item.dropdown ? (
                  <div 
                    className="relative"
                    onMouseEnter={() => setActiveDropdown(item.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <button
                      className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        isActive(item.href)
                          ? 'text-[#854DFF] bg-[#854DFF]/10'
                          : 'text-gray-700 hover:text-[#854DFF] hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                    {activeDropdown === item.name && (
                      <div className="absolute left-0 top-full w-56 bg-white rounded-md shadow-lg border border-gray-100 py-2 z-50">
                        {item.dropdown.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#854DFF]/10 hover:text-[#854DFF] transition-colors duration-200"
                            onClick={() => setActiveDropdown(null)}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                      isActive(item.href)
                        ? 'text-[#854DFF] bg-[#854DFF]/10'
                        : 'text-gray-700 hover:text-[#854DFF] hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/contact"
              className="bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:from-[#7A42E6] hover:to-[#6F37CC] transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-[#854DFF] hover:bg-gray-50 transition-colors duration-200"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100">
            <div className="space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={`block px-4 py-2 text-base font-medium rounded-md transition-colors duration-200 ${
                      isActive(item.href)
                        ? 'text-[#854DFF] bg-[#854DFF]/10'
                        : 'text-gray-700 hover:text-[#854DFF] hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  {item.dropdown && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.dropdown.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.name}
                          to={dropdownItem.href}
                          className="block px-4 py-1 text-sm text-gray-600 hover:text-[#854DFF] hover:bg-gray-50 rounded transition-colors duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {dropdownItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 mt-4 border-t border-gray-100 space-y-2">
                <Link
                  to="/contact"
                  className="block mx-4 px-4 py-2 bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white text-center rounded-lg font-semibold hover:from-[#7A42E6] hover:to-[#6F37CC] transition-all duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
